import { Component } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  CapCityItem,
  ContactType,
  CountryItem,
  DtoRegistryCreate,
  DtoRegistryEndCustomerCreate,
  DtoRegistryEndCustomerUpdate,
  ItalyCityEssential,
  ItalyCityItem,
  ItalyProvinceEssential,
  ItalyProvinceItem,
  ItalyRegionItem,
  ItalyRegionItemEssential,
  RegistryItem,
} from 'src/app/api/models';
import { RegistryService, CountryService, GeoService } from 'src/app/api/services';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { RegistryTypeEnum } from '../enums/registry_type_enum.enum';

@Component({
  selector: 'app-modal-registry-form',
  templateUrl: './modal-registry-form.component.html',
  styleUrls: ['./modal-registry-form.component.scss'],
})
export class ModalRegistryFormComponent {
  type?: ContactType[];
  country?: CountryItem[];
  selectedCountry: string | undefined;
  default_currency?: string[];
  registry_end_customer_id?: number;
  registry_end_customer?: RegistryItem;
  enumRegistryType = RegistryTypeEnum;
  regions: ItalyRegionItemEssential[] = [];
  provinces: ItalyProvinceEssential[] = [];
  cities: ItalyCityEssential[] = [];
  capCitiesOptions: CapCityItem[] = [];

  frm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    website: new FormControl<string | undefined>(undefined),
    main_contact: new FormControl<string | undefined>(undefined),
    email: new FormControl<string | undefined>(undefined, [Validators.email]),
    email_pec: new FormControl<string | undefined>(undefined, [Validators.email]),
    phone_number: new FormControl<string | undefined>(undefined),
    fax_number: new FormControl<string | undefined>(undefined),
    vat_number: new FormControl<string | undefined>(undefined),
    fiscal_code: new FormControl<string | undefined>(undefined),
    sdi: new FormControl<string | undefined>(undefined),
    contact_types: new FormControl<number[]>(
      [this.enumRegistryType.CLIENTE_FINALE],
      [Validators.required]
    ),
    address: new FormControl<string | undefined>(undefined, [Validators.required]),
    zip_code: new FormControl<string | undefined>(undefined, [
      Validators.required,
      Validators.maxLength(5),
    ]),
    country_iso: new FormControl('', [Validators.required]),
    default_currency: new FormControl('EUR', [Validators.required]),
    internal_code: new FormControl<string | undefined>(undefined, [Validators.required]),
    italy_region_id: new FormControl<number | undefined>(undefined, [Validators.required]),
    italy_province_id: new FormControl<number | undefined>(undefined, [Validators.required]),
    italy_city_id: new FormControl<number | undefined>(undefined, [Validators.required]),
    email_addresses: new FormArray([]),
  });

  constructor(
    public dialogService: DialogService,
    public auth: AuthService,
    private registryService: RegistryService,
    private countryService: CountryService,
    private geoService: GeoService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.registry_end_customer = config.data?.registry_end_customer;
  }

  ngOnInit() {
    this.frm.get('italy_region_id')!.valueChanges.subscribe(regionId => {
      if (!regionId) return;

      this.frm.get('italy_province_id')!.reset();
      this.frm.get('italy_city_id')!.reset();

      this.frm.get('italy_province_id')!.enable({ emitEvent: false });

      this.geoService.geoControllerGetProvincesByRegion({ id: regionId }).subscribe(res => {
        this.provinces = res.data.list;
      });
    });

    this.frm.get('italy_province_id')!.valueChanges.subscribe(provinceId => {
      if (!provinceId) return;

      this.frm.get('italy_city_id')!.reset();
      this.frm.get('italy_city_id')!.enable({ emitEvent: false });

      this.geoService.geoControllerGetProvince({ id: provinceId }).subscribe(res => {
        this.cities = res.data.cities;
      });
    });

    this.geoService.geoControllerGetRegions().subscribe(res => {
      this.regions = res.data.list;

      if (this.registry_end_customer) {
        this.patchFormBase();
        this.patchLocationForEdit();
      } else {
        this.frm
          .get('internal_code')!
          .setValue('C-' + Math.floor(Math.random() * 1000000).toString());
      }
    });

    this.frm.get('country_iso')?.valueChanges.subscribe(country => {
      if (country === 'IT') {
        this.frm.get('italy_region_id')?.disable({ emitEvent: false });
        this.frm.get('italy_province_id')?.disable({ emitEvent: false });
        this.frm.get('italy_city_id')?.disable({ emitEvent: false });
      } else {
        this.frm.get('italy_region_id')?.enable({ emitEvent: false });
        this.frm.get('italy_region_id')?.setValue(undefined, { emitEvent: false });
        this.frm.get('italy_province_id')?.setValue(undefined, { emitEvent: false });
        this.frm.get('italy_city_id')?.setValue(undefined, { emitEvent: false });
        this.capCitiesOptions = [];
      }
    });

    this.frm.get('zip_code')?.valueChanges.pipe(debounceTime(400)).subscribe(cap => {
      const country = this.frm.get('country_iso')?.value;
      const cleanCap = (cap || '').replace(/[^0-9]/g, '');
      if (country !== 'IT' || cleanCap.length !== 5) {
        this.capCitiesOptions = [];
        this.clearGeoFields();
        return;
      }
      this.geoService.geoControllerGetCitiesByCap({ cap: cleanCap }).subscribe(res => {
        const list = res.data?.list || [];
        if (list.length === 1) {
          this.applyCapResult(list[0]);
        } else if (list.length > 1) {
          this.capCitiesOptions = list;
        } else {
          this.capCitiesOptions = [];
          this.clearGeoFields();
        }
      });
    });

    this.default_currency = ['EUR', 'USD'];
    this.getCountries();
  }

  clearGeoFields() {
    this.frm.get('italy_region_id')?.setValue(undefined, { emitEvent: false });
    this.frm.get('italy_province_id')?.setValue(undefined, { emitEvent: false });
    this.frm.get('italy_city_id')?.setValue(undefined, { emitEvent: false });
    this.provinces = [];
    this.cities = [];
  }

  applyCapResult(result: CapCityItem) {
    this.capCitiesOptions = [];
    this.geoService.geoControllerGetProvincesByRegion({ id: result.region_id }).subscribe(res => {
      this.provinces = res.data.list;
      this.frm.get('italy_region_id')?.setValue(result.region_id, { emitEvent: false });
      this.geoService.geoControllerGetProvince({ id: result.province_id }).subscribe(cityRes => {
        this.cities = cityRes.data.cities;
        this.frm.get('italy_province_id')?.setValue(result.province_id, { emitEvent: false });
        this.frm.get('italy_city_id')?.setValue(result.city_id, { emitEvent: false });
      });
    });
  }
  private patchLocationForEdit() {
    const regionId = this.registry_end_customer!.delivery_italy_region?.id;
    const provinceId = this.registry_end_customer!.delivery_italy_province?.id;
    const cityId = this.registry_end_customer!.delivery_italy_city?.id;

    if (!regionId) return;

    this.frm.get('italy_region_id')?.setValue(regionId, { emitEvent: false });

    this.geoService.geoControllerGetProvincesByRegion({ id: regionId }).subscribe(res => {
      this.provinces = res.data.list;
      if (provinceId) {
        this.frm.get('italy_province_id')?.setValue(provinceId, { emitEvent: false });
        this.geoService.geoControllerGetProvince({ id: provinceId }).subscribe(cityRes => {
          this.cities = cityRes.data.cities;
          if (cityId) {
            this.frm.get('italy_city_id')?.setValue(cityId, { emitEvent: false });
          }
        });
      }
    });
  }

  private patchFormBase() {
    this.frm.patchValue({
      name: this.registry_end_customer!.name,
      website: this.registry_end_customer!.website,
      internal_code: this.registry_end_customer!.internal_code,
      main_contact: this.registry_end_customer!.main_contact,
      email: this.registry_end_customer!.email,
      phone_number: this.registry_end_customer!.phone_number,
      fax_number: this.registry_end_customer!.fax_number,
      email_pec: this.registry_end_customer!.email_pec,
      vat_number: this.registry_end_customer!.vat_number,
      fiscal_code: this.registry_end_customer!.fiscal_code,
      sdi: this.registry_end_customer!.sdi,
      default_currency: this.registry_end_customer!.default_currency,
      address: this.registry_end_customer!.address,
      zip_code: this.registry_end_customer!.zip_code,
      country_iso: this.registry_end_customer!.country?.ISO,
      contact_types: [3],
    });
  }

  getCountries() {
    this.countryService.countryControllerGetAll().subscribe(response => {
      this.country = response.data.list;
      this.frm.get('country_iso')!.setValue(response.data.list.find(p => p.name == 'Italia')!.ISO);
    });
  }

  close() {
    this.ref.close();
  }

  save() {
    const fv = this.frm.getRawValue();
    if (fv.country_iso === 'IT' && (!fv.italy_region_id || !fv.italy_province_id || !fv.italy_city_id)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Dati indirizzo incompleti',
        detail: 'Inserisci un CAP valido per popolare Regione, Provincia e Comune.',
      });
      return;
    }
    const body: DtoRegistryEndCustomerCreate = {
      name: fv.name!,
      website: fv.website ?? null,
      internal_code: fv.internal_code!,
      main_contact: fv.main_contact ?? null,
      email: fv.email ?? null,
      phone_number: fv.phone_number ?? null,
      fax_number: fv.fax_number ?? null,
      email_pec: fv.email_pec ?? null,
      vat_number: fv.vat_number ?? null,
      fiscal_code: fv.fiscal_code ?? null,
      sdi: fv.sdi ?? null,
      contact_types: [3],
      default_currency: fv.default_currency!,
      country_iso: fv.country_iso!,
      zip_code: fv.zip_code!,
      delivery_italy_region_id: fv.italy_region_id!,
      delivery_italy_province_id: fv.italy_province_id!,
      delivery_italy_city_id: fv.italy_city_id!,
      city: null,
      province: null,
      notes: null,
      office: null,
      office_code: null,
      email_addresses: [],
      services: [],
      address: fv.address!,
    };

    if (this.registry_end_customer) {
      const update_body: DtoRegistryEndCustomerUpdate = {
        address: fv.address ?? undefined,
        zip_code: fv.zip_code ?? undefined,

        delivery_italy_region_id: fv.italy_region_id ?? undefined,
        delivery_italy_province_id: fv.italy_province_id ?? undefined,
        delivery_italy_city_id: fv.italy_city_id ?? undefined,

        email: fv.email ?? undefined,
        email_pec: fv.email_pec ?? undefined,
        fax_number: fv.fax_number ?? undefined,
        fiscal_code: fv.fiscal_code ?? undefined,
        internal_code: fv.internal_code ?? undefined,
        main_contact: fv.main_contact ?? undefined,
        name: fv.name ?? undefined,
        phone_number: fv.phone_number ?? undefined,
        sdi: fv.sdi ?? undefined,
        vat_number: fv.vat_number ?? undefined,
        website: fv.website ?? undefined,
      };
      this.confirmationService.confirm({
        message: `Confermi l'aggiornamento dell'azienda?`,
        header: 'Attenzione',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sì',
        acceptButtonStyleClass: 'bg-danger',
        rejectButtonStyleClass: 'p-button-outlined',
        accept: () => {
          this.registryService
            .registryControllerUpdateRegistryEndCustomer({
              id: this.registry_end_customer!.id,
              body: update_body,
            })
            .subscribe({
              next: res => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Modifica cliente finale',
                  detail: 'Modifica cliente finale avvenuta con successo',
                });
                this.ref.close({ frmValue: res.data });
              },
              error: err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Modifica cliente finale',
                  detail: 'Errore nella modifica del cliente finale',
                });
              },
            });
        },
      });
    } else {
      //crea registry
      const create_body: DtoRegistryEndCustomerCreate = body;

      this.confirmationService.confirm({
        message: `Confermi la creazione dell'azienda?`,
        header: 'Attenzione',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sì',
        acceptButtonStyleClass: 'bg-danger',
        rejectButtonStyleClass: 'p-button-outlined',
        accept: () => {
          this.registryService
            .registryControllerCreateRegistryEndCustomer({ body: create_body })
            .subscribe({
              next: res => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Creazione cliente finale',
                  detail: 'Creazione cliente finale avvenuta con successo',
                });
                this.ref.close({ frmValue: res.data });
              },
              error: err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Creazione cliente finale',
                  detail: 'Errore nella creazione del cliente finale',
                });
              },
            });
        },
      });
    }
  }
}
